import React, { memo, useCallback } from 'react';
import { TextField, FormHelperText } from '@mui/material';

const FormTextField = memo(
  ({
    name,
    label,
    placeholder,
    formik,
    required,
    error: propError,
    helperText,
    onBlur,
    onChange,
    inputType,
    ...props
  }) => {
    const handleChange = useCallback(
      (e) => {
        const { value } = e.target;
        let sanitizedValue = value;

        if (inputType === 'numeric') {
          sanitizedValue = value.replace(/[^0-9]/g, '');
        } else if (inputType === 'text') {
          sanitizedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚüÜñÑçÇ\s'-]/g, '');
        } else if (inputType === 'alphanumeric') {
          sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '');
        } else if (inputType === 'fiscal') {
          sanitizedValue = value.replace(/[^0-9-]/g, '');
        }

        if (sanitizedValue !== value) {
          e.target.value = sanitizedValue;
        }

        formik.handleChange(e);
        onChange?.(e);
      },
      [formik, onChange, inputType]
    );

    const handleKeyPress = useCallback(
      (e) => {
        if (
          inputType === 'numeric' &&
          !/[0-9]/.test(e.key) &&
          e.key !== 'Backspace' &&
          e.key !== 'Delete' &&
          e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowRight' &&
          e.key !== 'Tab'
        ) {
          e.preventDefault();
        } else if (inputType === 'text' && /[0-9]/.test(e.key)) {
          e.preventDefault();
        } else if (
          inputType === 'alphanumeric' &&
          !/[a-zA-Z0-9]/.test(e.key) &&
          e.key !== 'Backspace' &&
          e.key !== 'Delete' &&
          e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowRight' &&
          e.key !== 'Tab'
        ) {
          e.preventDefault();
        } else if (
          inputType === 'fiscal' &&
          !/[0-9-]/.test(e.key) &&
          e.key !== 'Backspace' &&
          e.key !== 'Delete' &&
          e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowRight' &&
          e.key !== 'Tab'
        ) {
          e.preventDefault();
        }
      },
      [inputType]
    );

    const handleBlur = useCallback(
      (e) => {
        formik.handleBlur(e);
        onBlur?.(e);
      },
      [formik, onBlur]
    );

    const value = name.includes('.')
      ? name.split('.').reduce((obj, key) => obj?.[key], formik.values) || ''
      : formik.values[name] || '';

    const getError = () => {
      if (name.includes('.')) {
        return name.split('.').reduce((obj, key) => obj?.[key], formik.errors);
      }
      return formik.errors[name];
    };

    const getTouched = () => {
      if (name.includes('.')) {
        return name.split('.').reduce((obj, key) => obj?.[key], formik.touched);
      }
      return formik.touched[name];
    };

    const hasFormikError = getTouched() && Boolean(getError());
    const hasError = propError || hasFormikError;
    const errorMessage = getTouched() && getError();
    const displayHelperText = helperText || errorMessage || '';
    const fieldId = name.replace(/\./g, '-');

    const inputProps = {
      ...(props.inputProps || {}),
      ...(inputType === 'numeric'
        ? {
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }
        : {}),
    };

    return (
      <>
        <TextField
          {...props}
          error={hasError}
          fullWidth
          id={fieldId}
          inputProps={inputProps}
          label={label}
          margin='normal'
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          required={required}
          size='small'
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-error fieldset': {
                borderColor: 'error.main',
              },
            },
          }}
          value={value}
          variant='outlined'
          helperText={displayHelperText}
        />
      </>
    );
  }
);

FormTextField.displayName = 'FormTextField';

export default FormTextField;
